import React from "react";
import Icon from "./Icon";
import {
  roleLabel, sessionLabel, agoLabel, formatDay, formatStamp, initials, avatarColor, isBlocked, approveDisabledReason, NOTE_MAX,
} from "./signupsModel";

const STATUS_WORD = { PENDING: "pending approval", APPROVED: "approved", UNAPPROVED: "not approved" };

export function Avatar({ name, size = "sm" }) {
  return (
    <span className={`isg-av ${size}`} style={{ background: avatarColor(name || "") }} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

function Eligibility({ applicant, group }) {
  const id = `isg-el-${applicant.id}`;
  if (applicant.blockerKind === "CONFLICT") {
    return (
      <div className="isg-el bad" id={id}>
        <Icon name="alert" size={15} />
        <div><b>Schedule conflict</b><small>{applicant.blockerMessage}</small></div>
      </div>
    );
  }
  if (applicant.blockerKind === "AHA") {
    return (
      <div className="isg-el bad" id={id}>
        <Icon name="id" size={15} />
        <div><b>AHA instructor ID missing</b><small>{applicant.blockerMessage || "Required to teach AHA classes. Approval is blocked."}</small></div>
      </div>
    );
  }
  return (
    <div className="isg-el ok">
      <Icon name="checkC" size={15} />
      <div><b>No conflicts</b><small>Qualified for {roleLabel(group.teachingRole).toLowerCase()}</small></div>
    </div>
  );
}

function StatusChip({ applicant, inFlight }) {
  if (inFlight) {
    return <span className="isg-st isg-st-fly"><span className="isg-spin" />{applicant.status === "APPROVED" ? "Undoing" : "Assigning"}</span>;
  }
  if (applicant.status === "APPROVED") return <span className="isg-st isg-st-ok"><Icon name="check" size={12} />Approved</span>;
  if (applicant.status === "UNAPPROVED") return <span className="isg-st isg-st-no"><Icon name="x" size={12} />Not approved</span>;
  return <span className="isg-st isg-st-pend"><Icon name="clock" size={12} />Pending approval</span>;
}

function Actions({ applicant, group, busy, onApprove, onUnapprove }) {
  const name = applicant.instructorName;
  if (applicant.status === "APPROVED") {
    return (
      <button type="button" className="isg-btn isg-btn-o isg-btn-sm" disabled={busy} onClick={() => onUnapprove(applicant)}
        aria-label={`Unapprove ${name}`}>
        <Icon name="undo" size={13} />Unapprove
      </button>
    );
  }
  const why = approveDisabledReason(applicant, group);
  return (
    <>
      <button type="button" className={`isg-btn ${applicant.status === "UNAPPROVED" ? "isg-btn-o" : "isg-btn-p"} isg-btn-sm`}
        disabled={busy || Boolean(why)} title={why || undefined} aria-label={`Approve ${name}`}
        aria-describedby={isBlocked(applicant) ? `isg-el-${applicant.id}` : undefined}
        onClick={() => onApprove(applicant)}>
        <Icon name="check" size={12} />Approve
      </button>
      {applicant.status === "PENDING" && (
        <button type="button" className="isg-btn isg-btn-o isg-btn-sm" disabled={busy} onClick={() => onUnapprove(applicant)}
          aria-label={`Unapprove ${name}`}>
          Unapprove
        </button>
      )}
    </>
  );
}

function ConfirmStrip({ applicant, group, onGo, onCancel }) {
  const others = group.applicants.filter((a) => a.status === "PENDING" && a.id !== applicant.id).map((a) => a.instructorName);
  const list = others.length < 2 ? others.join("") : `${others.slice(0, -1).join(", ")} and ${others[others.length - 1]}`;
  return (
    <div className="isg-cf" role="group" aria-label="Confirm assignment">
      <p>
        Assign <b>{applicant.instructorName}</b> as {roleLabel(group.teachingRole).toLowerCase()} for {sessionLabel(group)}, {formatDay(group.sessionDate)}?
        <small>
          {others.length
            ? `${list} ${others.length > 1 ? "stay" : "stays"} pending and can't be approved while this slot is filled.`
            : "This fills the slot."}
        </small>
      </p>
      <button type="button" className="isg-btn isg-btn-p isg-btn-sm" onClick={onGo}>Assign to slot</button>
      <button type="button" className="isg-btn isg-btn-g isg-btn-sm" onClick={onCancel}>Cancel</button>
    </div>
  );
}

/** Reject (or unapprove) asks first, with an optional comment that goes in the instructor's email and on My Requests. */
function RejectStrip({ applicant, group, note, onNote, onGo, onCancel }) {
  const id = `isg-rj-${applicant.id}`;
  const verb = applicant.status === "APPROVED" ? "Unapprove" : "Reject";
  return (
    <div className="isg-cf isg-rj" role="group" aria-label="Confirm rejection">
      <p>
        {verb} <b>{applicant.instructorName}</b> for {sessionLabel(group)}, {formatDay(group.sessionDate)}?
        <small>They are emailed, and your comment (if you add one) is shown on their My Requests tab.</small>
      </p>
      <label className="isg-rj-l" htmlFor={id}>Comments for the instructor (optional)</label>
      <textarea id={id} data-testid={`isg-reject-note-${applicant.id}`} rows={3} maxLength={NOTE_MAX} value={note}
        placeholder="e.g. We already have a lead for these dates." onChange={(e) => onNote(e.target.value)} />
      <span className="isg-rj-c" aria-live="polite">{note.length}/{NOTE_MAX}</span>
      <button type="button" className="isg-btn isg-btn-p isg-btn-sm" data-testid="isg-reject-send" onClick={onGo}>Send decision</button>
      <button type="button" className="isg-btn isg-btn-g isg-btn-sm" onClick={onCancel}>Cancel</button>
    </div>
  );
}

/** One applicant on a slot, with its confirm / in-flight / error strips underneath. */
export default function ApplicantRow({ applicant, group, ui, now, onApprove, onUnapprove, onGo, onCancel, onDismiss, onNote, onRejectGo }) {
  const inFlight = ui.flight === applicant.id;
  const busy = Boolean(ui.flight);
  const filledByOther = group.status === "FILLED" && applicant.status !== "APPROVED";
  const cls = ["isg-app", applicant.status === "APPROVED" && "isok", inFlight && "fly", (applicant.status === "UNAPPROVED" || filledByOther) && "mute"]
    .filter(Boolean).join(" ");

  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const rows = [...e.currentTarget.parentElement.querySelectorAll("[data-isg-row]")];
      const next = rows[rows.indexOf(e.currentTarget) + (e.key === "ArrowDown" ? 1 : -1)];
      if (next) next.focus();
      return;
    }
    const key = e.key.toLowerCase();
    const button = e.currentTarget.querySelector(key === "a" ? "[aria-label^='Approve']" : key === "u" ? "[aria-label^='Unapprove']" : "[data-none]");
    if (button && !button.disabled) button.click();
  };

  return (
    <>
      <div className={cls} role="listitem" tabIndex={0} data-isg-row
        aria-label={`${applicant.instructorName}, ${STATUS_WORD[applicant.status]}${isBlocked(applicant) ? ", blocked" : ""}`}
        onKeyDown={onKeyDown}>
        <div className="isg-who">
          <Avatar name={applicant.instructorName} />
          <div>
            <b>{applicant.instructorName}</b>
            <span className="isg-sess">{sessionLabel(group)}</span>
            <small>Applied {formatStamp(applicant.requestedAt)}<br />{agoLabel(applicant.requestedAt, now)} ago</small>
          </div>
        </div>
        <Eligibility applicant={applicant} group={group} />
        <div className="isg-stc"><StatusChip applicant={applicant} inFlight={inFlight} /></div>
        <div className="isg-ac"><Actions applicant={applicant} group={group} busy={busy} onApprove={onApprove} onUnapprove={onUnapprove} /></div>
      </div>
      {ui.confirm === applicant.id && <ConfirmStrip applicant={applicant} group={group} onGo={onGo} onCancel={onCancel} />}
      {ui.reject === applicant.id && (
        <RejectStrip applicant={applicant} group={group} note={ui.note || ""} onNote={onNote} onGo={onRejectGo} onCancel={onCancel} />
      )}
      {applicant.status === "UNAPPROVED" && applicant.decisionNote && (
        <div className="isg-sent" data-testid={`isg-sent-note-${applicant.id}`}>
          <b>Comment sent to {applicant.instructorName}:</b> {applicant.decisionNote}
        </div>
      )}
      {inFlight && applicant.status !== "APPROVED" && (
        <div className="isg-cf">
          <span className="isg-spin" style={{ color: "var(--color-accent-violet)" }} />
          <p>Assigning <b>{applicant.instructorName}</b> to this slot…</p>
        </div>
      )}
      {ui.err && ui.err.a === applicant.id && (
        <div className="isg-er" role="alert">
          <Icon name="alert" size={15} />
          <p><b>Couldn&apos;t assign {applicant.instructorName}.</b> <span>{ui.err.msg}</span></p>
          <button type="button" className="isg-btn isg-btn-o isg-btn-sm" onClick={onDismiss}>Dismiss</button>
        </div>
      )}
    </>
  );
}
